import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SERVICE_PAGES } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import { useHreflang, useSeo } from "@/components/seo/Seo";

const ease = [0.22, 1, 0.36, 1];

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

// slug passé en prop par le prerender (pas de useParams côté Node)
export default function ServicePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { t, lang } = useLang();
  const cfg = SERVICE_PAGES[slug];
  const c = cfg ? t(`svc.${cfg.id}`) : null;
  useSeo({
    lang,
    title: c ? `${c.name} | MILES` : "MILES",
    description: c ? c.sub : "",
    path: `/${lang}/services/${slug}`,
    type: "service", id: slug,
  });
  useHreflang(cfg ? { fr: `/fr/services/${slug}`, en: `/en/services/${slug}`, de: `/de/services/${slug}` } : null);
  if (!cfg) return <Navigate to={`/${lang}/services`} replace />;

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={`service-page-${cfg.id}`}>
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pb-24 lg:pt-40" data-testid="svc-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={16}>
            <Link to="/services" data-testid="svc-back"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate transition-colors hover:text-miles">
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
              {t("sv.back")}
            </Link>
          </FadeIn>
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <FadeIn y={20}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("sv.eyebrow")} — {c.name}</p>
              <h1 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                {c.h1a}
                <br />
                <span className="font-light text-slate/55">{c.h1b}</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate sm:text-lg">{c.sub}</p>
              <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 border-t border-ink/10 pt-6" data-testid="svc-usps">
                {c.usps.map((item) => (
                  <p key={item} className="flex items-center gap-2.5 text-sm font-medium text-ink/80">
                    <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {item}
                  </p>
                ))}
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
                <Link to={cfg.cta} data-testid="svc-hero-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(36,87,255,0.3)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                  {c.final_cta} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <p className="font-mono text-sm font-semibold text-ink" data-testid="svc-price">{c.price_v}</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.12} className="relative">
              <div className="overflow-hidden rounded-[2rem] border border-ink/10 shadow-[0_40px_90px_-50px_rgba(11,31,77,0.4)]">
                <img src={cfg.image} alt={c.name} loading="lazy" className="img-clean aspect-[4/3] w-full object-cover" />
              </div>
              <span className="glass absolute bottom-5 left-5 rounded-full px-4 py-1.5 text-xs font-bold text-ink">{c.price_v}</span>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="svc-included">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("sv.inc_eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.02] tracking-[-0.045em] text-ink sm:text-4xl lg:text-5xl">
              {t("sv.inc_h2a")}{" "}
              <span className="font-light text-slate/55">{t("sv.inc_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-12 border-t border-ink/10">
            {c.included.map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.05} y={16}>
                <div data-testid={`svc-included-item-${i}`}
                  className="group grid gap-2 border-b border-ink/10 py-8 sm:grid-cols-[64px_1fr_1.1fr] sm:items-baseline sm:gap-10">
                  <span className="font-mono text-xs font-semibold tracking-[0.2em] text-miles">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-xl font-semibold tracking-tight text-ink transition-transform duration-300 group-hover:translate-x-1.5 sm:text-2xl">{title}</p>
                  <p className="text-sm leading-relaxed text-slate">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cloud py-20 lg:py-28" data-testid="svc-steps">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("sv.steps_eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.02] tracking-[-0.045em] text-ink sm:text-4xl lg:text-5xl">
              {t("sv.steps_h2a")}{" "}
              <span className="font-light text-slate/55">{t("sv.steps_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-px overflow-hidden rounded-[2rem] border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {c.steps.map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-white p-7" data-testid={`svc-step-${i}`}>
                  <p className="font-mono text-[11px] font-semibold tracking-[0.25em] text-miles">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 text-lg font-semibold tracking-tight text-ink">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-20 lg:py-28" data-testid="svc-final-section">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-miles-electric/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("sv.price_t")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl">
              {c.price_v}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">{c.price_note}</p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link to={cfg.cta} data-testid="svc-final-cta"
                className="inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(36,87,255,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                {c.final_cta} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/flotte" data-testid="svc-fleet-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-white/85 transition-colors hover:text-white">
                {t("sv.fleet_link")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
